import { widgetTargetLabel } from "../widgets/target-key-label.js";

/**
 * PB-4923 AC6 — what an APPLIED proposal actually did, per operation.
 *
 * The executor judges every operation on its own, so "applied" is routinely a
 * partial truth: three causes added, one field left alone because a colleague
 * is in it. The card showed only the status pill, so the skipped part was
 * invisible — the result object reached the payload as `executionResult` and
 * nothing read it.
 *
 * This is the client half of the same fold the Python does
 * (`runtime_v2/widget_outcome.py`), and it exists separately rather than
 * consuming that module's sentence because only the client can name things
 * properly: it has the widget's own content, so `branches[br-1]/causes[c-2]/text`
 * becomes "text of “Machine calibration”", and it has the roster, so an actor id
 * becomes a person. The Python's `outcome_message` stays the server-side record
 * for a Run with neither.
 *
 * Folds BOTH result shapes: a single command answers `{applied, skipped}`, and
 * the canvas bundle answers `{change_count, changes: [...]}` where each change
 * carries its own pair. A bundle that reported only its envelope would hide
 * exactly the case this exists for.
 *
 * `null` means "nothing to say" — a clean apply, or a legacy action that
 * reports no per-operation detail. The caller renders nothing, which is right:
 * the status pill already says Applied.
 */

/** Skip reasons, phrased for a reader rather than a log. */
const REASON_SENTENCE: Record<
  string,
  (field: string | null, who: string) => string
> = {
  editing_in_progress: (field, who) =>
    field === null
      ? `${who} is editing it right now.`
      : `${who} is editing ${field} right now.`,
  field_changed: (field, who) =>
    field === null
      ? `${who} changed it first.`
      : `${who} changed ${field} first.`,
  item_deleted: (field) =>
    field === null ? "It was deleted." : `${field} was deleted.`,
  invalid: () => "It would have left the widget in an invalid state.",
  capacity_exceeded: () => "That list is already at its maximum size.",
  unsupported_target: () =>
    "That part of the widget can't be changed this way.",
  dependency_skipped: () => "It was part of a change that couldn't be applied.",
};

export interface ProposalSkippedOperation {
  readonly operationId: string | null;
  readonly reason: string;
  readonly targetKey: string | null;
  readonly actorId: string | null;
  /**
   * The widget this skip belongs to, when the result names one.
   *
   * A bundle's changes are each a whole single-command result carrying their
   * OWN `widget_id`, so a two-widget bundle must resolve two different sets of
   * params. Reading one widget's content to label another's field would name
   * the wrong thing confidently, which is worse than not naming it — and
   * discarding all context because there is more than one widget would throw
   * away an answer that is available per change.
   */
  readonly widgetId: string | null;
}

export interface ProposalApplyOutcome {
  /** `partial` = some landed and some did not; `skipped` = none landed. */
  readonly status: "partial" | "skipped";
  readonly appliedCount: number;
  readonly skipped: readonly ProposalSkippedOperation[];
  /** One line stating the shape of the result. */
  readonly headline: string;
  /** One sentence per skip, in the order the server reported them. */
  readonly reasons: readonly string[];
}

export interface ProposalApplyOutcomeOptions {
  /** Resolves a user id to a display name, or `null` when it cannot. */
  readonly nameForActor?: (userId: string) => string | null;
  /**
   * The widget's params, so a field can be named from its own content. Used
   * for a single-command result, and as the fallback for a change whose own
   * widget cannot be resolved.
   */
  readonly params?: unknown;
  /**
   * Params BY widget id, for a bundle whose changes span several widgets.
   * Consulted first; `undefined` for a widget the caller cannot supply falls
   * back to `params`, then to naming the field by its key.
   */
  readonly paramsForWidget?: (widgetId: string) => unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

/** Fold one result — single command or canvas bundle — into totals. */
function collect(result: Record<string, unknown>): {
  applied: number;
  skipped: ProposalSkippedOperation[];
} {
  const changes = asArray(result["changes"]);
  if (changes.length > 0) {
    let applied = 0;
    const skipped: ProposalSkippedOperation[] = [];
    for (const change of changes) {
      const child = asRecord(change);
      if (child === null) continue;
      const folded = collect(child);
      applied += folded.applied;
      skipped.push(...folded.skipped);
    }
    return { applied, skipped };
  }

  // Each change of a bundle is a whole single-command result, so the widget it
  // belongs to is named right here — which is what lets a two-widget bundle
  // label each field against its OWN content.
  const widgetId = asString(result["widget_id"]);
  const skipped: ProposalSkippedOperation[] = [];
  for (const entry of asArray(result["skipped"])) {
    const row = asRecord(entry);
    if (row === null) continue;
    skipped.push({
      operationId: asString(row["operation_id"]),
      reason: asString(row["reason"]) ?? "unknown",
      targetKey: asString(row["target_key"]),
      actorId: asString(row["actor_id"]),
      widgetId,
    });
  }
  return { applied: asArray(result["applied"]).length, skipped };
}

/**
 * Summarise an apply result, or `null` when there is nothing to report.
 */
export function proposalApplyOutcome(
  result: unknown,
  options: ProposalApplyOutcomeOptions = {},
): ProposalApplyOutcome | null {
  const record = asRecord(result);
  if (record === null) return null;

  const { applied, skipped } = collect(record);
  if (skipped.length === 0) return null;

  const reasons = skipped.map((skip) => {
    // `widgetTargetLabel` returns a quoted, content-derived label and declines
    // rather than print an id, so an unnameable target degrades to impersonal
    // wording instead of leaking `causes[c-2]` into a sentence.
    /* Params PER change: a bundle touching two widgets must label each field
       against its own widget's content. Naming one from the other's would be
       confidently wrong, and falling back to nothing for the whole bundle
       would discard context that is available change by change. */
    const params =
      (skip.widgetId !== null
        ? options.paramsForWidget?.(skip.widgetId)
        : undefined) ?? options.params;
    const field =
      skip.targetKey === null
        ? null
        : widgetTargetLabel(skip.targetKey, params);
    const name =
      skip.actorId === null
        ? null
        : asString(options.nameForActor?.(skip.actorId));
    // "Someone else", never an id fragment. Naming the wrong person is worse
    // than naming none — the same rule the human-save path applies.
    const who = name ?? "Someone else";
    const sentence = REASON_SENTENCE[skip.reason];
    return sentence
      ? sentence(field, who)
      : field === null
        ? "It couldn't be applied."
        : `${field} couldn't be applied.`;
  });

  const count = skipped.length;
  const noun = `${count} change${count === 1 ? "" : "s"}`;
  if (applied === 0) {
    return {
      status: "skipped",
      appliedCount: 0,
      skipped,
      headline: `Nothing was changed — ${noun} skipped.`,
      reasons,
    };
  }
  return {
    status: "partial",
    appliedCount: applied,
    skipped,
    headline: `Applied ${applied} change${applied === 1 ? "" : "s"} · ${noun} skipped.`,
    reasons,
  };
}
